/**
 * Small, deterministic HaxBall-style physics stepper for analysis previews.
 *
 * It follows the ordering used by the native engine: process player input and
 * kicks, advance discs, apply gravity/damping, then resolve map and disc
 * collisions. This is deliberately a plain-data module; the replay engine
 * remains authoritative for rendered matches.
 */

import { resolveNativeSegmentCollision } from './haxballNativeCollision.js';

const EPSILON = 1e-8;
const DEFAULT_RADIUS = 15;
const DEFAULT_BALL_RADIUS = 10;
// Native `all` is the six standard groups (63), not every bit. Including
// c0–c3 makes ordinary discs collide with BFF's restricted defensive planes.
const ALL_MASK = 63;
const MASKS = { all: ALL_MASK, ball: 1, red: 2, blue: 4, redko: 8, blueko: 16, wall: 32, kick: 64, score: 128,
  c0: 268435456, c1: 536870912, c2: 1073741824, c3: -2147483648 };

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const vec = (value, fallback = { x: 0, y: 0 }) => {
  if (Array.isArray(value)) return { x: finite(value[0]), y: finite(value[1]) };
  if (!value || typeof value !== 'object') return { ...fallback };
  return { x: finite(value.x ?? value[0]), y: finite(value.y ?? value[1]) };
};
const add = (a, b) => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
const mul = (a, n) => ({ x: a.x * n, y: a.y * n });
const length = (a) => Math.hypot(a.x, a.y);
const normal = (a, fallback = { x: 1, y: 0 }) => {
  const n = length(a);
  return n > EPSILON ? mul(a, 1 / n) : { ...fallback };
};

function mask(value, fallback = ALL_MASK) {
  if (value === undefined || value === null) return fallback;
  if (Array.isArray(value)) return value.reduce((result, item) => result | mask(item, 0), 0);
  if (typeof value === 'string') {
    return value.split(/[ ,|]/).filter(Boolean).reduce((result, item) => result | (MASKS[item.toLowerCase()] || 0), 0);
  }
  return Number(value) | 0;
}

function cloneState(input = {}) {
  const source = input || {};
  const stadium = source.stadium || source.map || {};
  const physics = stadium.playerPhysics || {};
  const ballSource = source.ball || source.ballState || {};
  const normalDisc = (raw, radius) => {
    const value = raw || {};
    return {
    ...(value),
    position: vec(value.position || value.pos || value.a),
    velocity: vec(value.velocity || value.vel || value.D),
    gravity: vec(value.gravity || value.oa),
    radius: finite(value.radius ?? value.Z, radius),
    bCoef: finite(value.bCoef ?? value.m, .5),
    invMass: finite(value.invMass ?? value.aa, 1),
    damping: finite(value.damping ?? value.Ca, .99),
    cMask: mask(value.cMask ?? value.h),
    cGroup: mask(value.cGroup ?? value.v)
    };
  };
  const players = (Array.isArray(source.players) ? source.players : source.playerStates || []).map((raw, index) => {
    const player = normalDisc(raw || {}, finite(raw?.radius ?? raw?.H?.Z ?? physics.radius, DEFAULT_RADIUS));
    player.id = raw?.id ?? raw?.playerId ?? raw?.V ?? index;
    player.team = raw?.team ?? raw?.teamKey ?? raw?.ea?.w ?? null;
    const team = String(player.team).toLowerCase();
    const teamGroup = team === 'red' || team === '1' ? MASKS.red : team === 'blue' || team === '2' ? MASKS.blue : 0;
    player.cMask = mask(raw?.cMask ?? raw?.h ?? raw?.H?.h, 39);
    player.cGroup = mask(raw?.cGroup ?? raw?.v ?? raw?.H?.v, teamGroup | mask(physics.cGroup ?? physics.v, 0));
    player.active = raw?.active !== false && raw?.spectator !== true;
    player.input = vec(raw?.input || { x: raw?.inputX, y: raw?.inputY });
    player.facing = vec(raw?.facing || raw?.direction || player.input, { x: 1, y: 0 });
    player.kickCooldown = Math.max(0, finite(raw?.kickCooldown ?? raw?.cooldown ?? raw?.Sc, 0));
    player.kickBudget = finite(raw?.kickBudget ?? raw?.yc, NaN);
    player.kickHeld = Boolean(raw?.kickHeld);
    player.kickStrength = finite(raw?.kickStrength ?? physics.kickStrength ?? physics.Re, 5);
    player.kickback = finite(raw?.kickback ?? physics.kickback ?? physics.Se, 0);
    player.kickRate = raw?.kickRate || source.kickRate || physics.kickRate || {};
    player.gravity = vec(raw?.gravity || raw?.oa || physics.gravity);
    player.radius = finite(raw?.radius ?? raw?.H?.Z ?? physics.radius, DEFAULT_RADIUS);
    player.bCoef = finite(raw?.bCoef ?? raw?.H?.m ?? physics.bCoef, .5);
    player.invMass = finite(raw?.invMass ?? raw?.H?.aa ?? physics.invMass, .5);
    player.damping = finite(raw?.damping ?? raw?.H?.Ca ?? physics.damping, .96);
    return player;
  });
  const normalizedStadium = { ...stadium, playerPhysics: { ...physics } };
  normalizedStadium.segments = Array.isArray(stadium.segments) ? stadium.segments.map(segment => ({ ...segment })) : [];
  normalizedStadium.vertexes = Array.isArray(stadium.vertexes) ? stadium.vertexes.map(vertex => ({ ...vertex })) : [];
  normalizedStadium.planes = Array.isArray(stadium.planes) ? stadium.planes.map(plane => ({ ...plane })) : [];
  normalizedStadium.discs = Array.isArray(stadium.discs) ? stadium.discs.map(disc => normalDisc(disc, finite(disc?.radius, 0))) : [];
  return {
    ...source,
    tick: finite(source.tick ?? source.replayTick ?? source.frame, 0),
    players,
    ball: { ...normalDisc(ballSource, DEFAULT_BALL_RADIUS), cGroup: mask(ballSource.cGroup ?? ballSource.v, 193) },
    stadium: normalizedStadium
  };
}

function collides(a, b) {
  return (mask(a.cMask) & mask(b.cGroup)) !== 0 && (mask(b.cMask) & mask(a.cGroup)) !== 0;
}

function resolveSegment(disc, segment, stadium) {
  return resolveNativeSegmentCollision(disc, segment, stadium.vertexes || []);
}

function resolvePlane(disc, plane) {
  const n = normal(vec(plane.normal || plane.n || plane.ya), { x: 1, y: 0 });
  const distance = finite(plane.dist ?? plane.distance ?? plane.Va, 0);
  const signed = disc.position.x * n.x + disc.position.y * n.y - distance;
  if (signed >= disc.radius) return false;
  disc.position = add(disc.position, mul(n, disc.radius - signed));
  const inward = disc.velocity.x * n.x + disc.velocity.y * n.y;
  if (inward < 0) disc.velocity = sub(disc.velocity, mul(n, inward * (1 + disc.bCoef * finite(plane.bCoef ?? plane.m, 1))));
  return true;
}

function resolveDiscPair(a, b) {
  const delta = sub(a.position, b.position);
  const distance = length(delta);
  const minimum = a.radius + b.radius;
  if (distance >= minimum) return false;
  const n = normal(delta);
  const penetration = minimum - distance;
  const massA = a.invMass > 0 ? a.invMass : 0;
  const massB = b.invMass > 0 ? b.invMass : 0;
  const total = massA + massB || 1;
  a.position = add(a.position, mul(n, penetration * massA / total));
  b.position = sub(b.position, mul(n, penetration * massB / total));
  const relative = sub(a.velocity, b.velocity);
  const inward = relative.x * n.x + relative.y * n.y;
  if (inward < 0) {
    // Native disc impulses use the product of the two bounce coefficients
    // (`a.m * b.m + 1`), including when one disc is a static keeper/map disc.
    const restitution = 1 + a.bCoef * b.bCoef;
    const impulse = inward * restitution / total;
    a.velocity = sub(a.velocity, mul(n, impulse * massA));
    b.velocity = add(b.velocity, mul(n, impulse * massB));
  }
  return true;
}

function inputVector(input) {
  const value = vec(input);
  const n = length(value);
  if (n < EPSILON) return { x: 0, y: 0 };
  // Native controls quantise to eight directions and normalise diagonals.
  const x = value.x === 0 ? 0 : value.x > 0 ? 1 : -1;
  const y = value.y === 0 ? 0 : value.y > 0 ? 1 : -1;
  const diagonal = x !== 0 && y !== 0 ? Math.SQRT1_2 : 1;
  return { x: x * diagonal, y: y * diagonal };
}

function canKick(player, controls) {
  const ball = controls.ball;
  if (!ball || player.kickCooldown > 0) return false;
  const gap = length(sub(ball.position, player.position)) - player.radius - ball.radius;
  if (!(gap < 4)) return false;
  if (Number.isFinite(player.kickBudget)) {
    const rate = player.kickRate || {};
    const minimum = finite(rate.min ?? rate.minimum, 0);
    if (player.kickBudget < minimum) return false;
  }
  return Boolean(controls.kick);
}

export function advancePhysics(inputState = {}, controls = {}, options = {}) {
  const state = cloneState(inputState);
  const events = [];
  const playerPhysics = state.stadium.playerPhysics || {};
  const frozen = new Set((options.frozenPlayerIds || []).map(String));
  const playerControls = controls || {};
  // HaxBall tests kick input before applying this tick's player movement.
  // This ordering matters at the edge of kick range: walking into the ball
  // cannot also kick it on that same tick.
  state.players.forEach(player => {
    if (!player.active || frozen.has(String(player.id))) return;
    player.kickCooldown = Math.max(0, player.kickCooldown - 1);
    if (Number.isFinite(player.kickBudget)) {
      const rate = player.kickRate || {};
      player.kickBudget = Math.min(finite(rate.max ?? rate.burst, player.kickBudget), player.kickBudget + finite(rate.rate ?? rate.recharge, 1));
    }
  });

  state.players.forEach(player => {
    if (!player.active || frozen.has(String(player.id))) return;
    const control = playerControls[player.id] || playerControls[String(player.id)] || {};
    if (!canKick(player, { ...control, ball: state.ball })) return;
    const direction = normal(sub(state.ball.position, player.position), player.facing);
    const strength = finite(player.kickStrength ?? playerPhysics.kickStrength ?? playerPhysics.Re, 5);
    state.ball.velocity = add(state.ball.velocity, mul(direction, strength * state.ball.invMass));
    const kickback = finite(player.kickback ?? playerPhysics.kickback ?? playerPhysics.Se, 0);
    if (kickback) player.velocity = sub(player.velocity, mul(direction, kickback * player.invMass));
    const rate = player.kickRate || {};
    player.kickCooldown = Math.max(1, finite(rate.cooldown ?? rate.min, 1));
    if (Number.isFinite(player.kickBudget)) player.kickBudget -= finite(rate.min ?? rate.minimum, 0);
    events.push({ type: 'kick', playerId: player.id, direction, strength });
  });

  state.players.forEach(player => {
    if (!player.active || frozen.has(String(player.id))) return;
    const control = playerControls[player.id] || playerControls[String(player.id)] || {};
    const input = inputVector(control.input || control);
    player.input = input;
    if (input.x || input.y) player.facing = input;
    // `kickHeld` is an input bit, not the native one-tick Wb latch. Using it
    // here would keep kicking acceleration active for the entire captured
    // horizon whenever a player held the key. The parity adapter supplies the
    // rising-edge latch as control.kick.
    const kicking = Boolean(control.kick);
    const acceleration = finite(kicking ? (playerPhysics.kickingAcceleration ?? playerPhysics.gf) : (playerPhysics.acceleration ?? playerPhysics.Ce), kicking ? .07 : .1);
    const damping = finite(kicking ? (playerPhysics.kickingDamping ?? playerPhysics.hf) : (player.damping ?? playerPhysics.damping ?? playerPhysics.Ca), .96);
    player.velocity.x += input.x * acceleration;
    player.velocity.y += input.y * acceleration;
    player.position = add(player.position, player.velocity);
    player.velocity = add(player.velocity, player.gravity);
    player.velocity = mul(player.velocity, damping);
  });

  // Frozen players keep their authoritative position but remain physical
  // obstacles. This is useful for previews that animate one carrier against
  // a paused opponent formation.
  const dynamic = state.players.filter(player => player.active);
  const frozenDiscs = new Map(dynamic.filter(player => frozen.has(String(player.id)))
    .map(player => [player, { invMass: player.invMass, velocity: player.velocity }]));
  frozenDiscs.forEach((_, player) => { player.invMass = 0; player.velocity = { x: 0, y: 0 }; });
  // Players were integrated above. Integrate the ball here exactly once; a
  // second player integration makes previews run at roughly double speed.
  const movingDiscs = [state.ball];
  movingDiscs.forEach(disc => {
    disc.position = add(disc.position, disc.velocity);
    disc.velocity = add(disc.velocity, disc.gravity);
    disc.velocity = mul(disc.velocity, disc.damping);
  });
  const map = state.stadium;
  // Resolve map geometry before disc pairs. This matches the replay's stable
  // wall correction when a player and ball start the tick inside a post or
  // goal line, and prevents a player contact from hiding that wall bounce.
  [state.ball, ...dynamic].forEach(disc => {
    // Native map correction applies only to movable discs. Paused preview
    // players remain obstacles at their recorded positions.
    if (disc.invMass === 0) return;
    (map.planes || []).forEach((plane, index) => {
      if (collides(disc, plane) && resolvePlane(disc, plane)) events.push({ type: 'collision', kind: 'plane', index });
    });
    (map.segments || []).forEach((segment, index) => {
      if (collides(disc, segment) && resolveSegment(disc, segment, map)) events.push({ type: 'collision', kind: 'segment', index });
    });
    (map.vertexes || []).forEach(vertex => {
      const point = vec(vertex.position || vertex);
      // Native vertexes are point obstacles; their effective radius is the
      // moving disc radius, so a zero-radius guard would silently remove all
      // corner and goal-post contacts.
      const staticDisc = { position: point, velocity: { x: 0, y: 0 }, radius: finite(vertex.radius ?? vertex.Z, 0), bCoef: finite(vertex.bCoef ?? vertex.m, 1), invMass: 0 };
      if (collides(disc, vertex) && resolveDiscPair(disc, staticDisc)) events.push({ type: 'collision', kind: 'vertex' });
    });
    (map.discs || []).forEach((staticDisc, index) => {
      if (collides(disc, staticDisc) && resolveDiscPair(disc, staticDisc)) events.push({ type: 'collision', kind: 'disc', index });
    });
  });
  for (let i = 0; i < dynamic.length; i += 1) {
    for (let j = i + 1; j < dynamic.length; j += 1) {
      if (collides(dynamic[i], dynamic[j]) && resolveDiscPair(dynamic[i], dynamic[j])) {
        events.push({ type: 'collision', kind: 'player', playerId: dynamic[i].id, otherPlayerId: dynamic[j].id });
      }
    }
    if (collides(state.ball, dynamic[i]) && resolveDiscPair(state.ball, dynamic[i])) {
      events.push({ type: 'contact', playerId: dynamic[i].id });
    }
  }
  frozenDiscs.forEach(({ invMass, velocity }, player) => { player.invMass = invMass; player.velocity = velocity; });
  state.tick += 1;
  if ((map.joints || []).length) events.push({ type: 'warning', code: 'joints-unsupported', count: map.joints.length });
  return { state, events };
}

export const stepPhysics = advancePhysics;
